import React, { useState } from 'react';
import {
  setTenantDefaultLanguage,
  SUPPORTED_LANGUAGES,
} from '../../../services/translationsAdminService';
// feat-260 — ONE added line, plus this import. See the `<p>` below.
import { DEFAULT_LANGUAGE_EYEBROW, EYEBROW_CLASSNAME } from './tenantLanguageEyebrows';

/**
 * Per-tenant default-language editor (feat-037-spanish-onboarding, ADR 0003 Q4).
 *
 * Sets `tenant.default_language`, the fourth (last) tier of the resolution stack
 * (server.preferred_locale → location.default_language → tenant.default_language →
 * 'en'). Wired to the existing PUT /api/tenant/{tid} extended with a
 * `default_language` field (already persisted + validated against SUPPORTED_LOCALES
 * by the backend — tenant_management.py:874).
 *
 * This mirrors `LocationLanguageDefaultCard` (the per-location picker) for
 * consistency — same dropdown, same save flow, same RBAC. Reuse, not new design.
 *
 * RBAC: rendered only when the parent grants `canManage` (StaffRoute +
 * tenant-scoped corporate/admin). Store managers cannot set a tenant default —
 * corporate/admin only — by design, matching the backend permission check
 * (update_tenant requires Admin or CorporateManager of the tenant).
 */
const TenantLanguageDefaultCard: React.FC<{
  tenantId: string;
  initialLanguage?: string;
  canManage: boolean;
}> = ({ tenantId, initialLanguage, canManage }) => {
  const [lang, setLang] = useState<string>(initialLanguage ?? 'en');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setNote(null);
    try {
      await setTenantDefaultLanguage(tenantId, lang);
      setNote('Saved.');
    } catch (e) {
      setNote(`Could not save: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="tenant-language-default-card"
    >
      {/* feat-260 (spec AT12, APPROVED 2026-08-25) — the ONLY change this feature
          makes to this card. Half of a matched eyebrow pair; its partner is above
          "Translate content into" on the card immediately below this one.

          DO NOT DELETE THIS LINE WITHOUT DELETING ITS PARTNER. It is what tells a
          reader which of two near-identical cards controls what a trainee SEES and
          which controls what gets TRANSLATED — the two were a single setting until
          feat-260, and that conflation was the bug. One eyebrow with no partner is
          worse than none: it implies a system its neighbour is failing to follow.

          `design/argued-lines.md` A2 flags this as the likeliest line in the
          feature to be tidied away by an unrelated PR, since nothing else in this
          file depends on it. It is held by
          `TenantDashboard.languageEyebrows.test.tsx`, which renders both cards. */}
      <p className={EYEBROW_CLASSNAME}>{DEFAULT_LANGUAGE_EYEBROW}</p>
      <h2 className="text-lg font-semibold text-ink mb-1">Default language</h2>
      <p className="text-sm text-inkMuted mb-3">
        The language trainees in this tenant see when neither they nor their
        location has set a preference. Falls back to English.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="text-sm border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          disabled={!canManage || saving}
          data-testid="tenant-default-language-select"
          aria-label="Tenant default language"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={!canManage || saving}
          onClick={handleSave}
          data-testid="tenant-default-language-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {note && <span className="text-xs text-inkMuted">{note}</span>}
      </div>
      {!canManage && (
        <p className="mt-2 text-xs text-inkMuted">
          Only corporate or admin users can change the tenant default language.
        </p>
      )}
    </div>
  );
};

export default TenantLanguageDefaultCard;
